import { useCallback, useMemo } from "react";

import { colors } from "theme/palette";

import ClickAwayListener from "components/ClickAwayListener";
import { TableCell } from "components/Table";
import Text from "components/Text";
import TextField from "components/TextField";

interface ICell {
  cellKey: string;
  value: string;
  colSpan?: number;
  editable?: boolean;
  textAlign?: "left" | "right" | "center";
  onChangeCellValue?: ({ value, key }: { value: string; key: string }) => void;
  dataTestid?: string;
  textStyle?: { [key: string]: string | number } | null;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTableCellElement>) => void;
  isCurrentlyActive?: boolean;
  manageCellActiveStatus?: ({ activate }: { activate: boolean }) => void;
}

const Cell = ({
  cellKey: key,
  value,
  editable,
  colSpan,
  textAlign = "left",
  onChangeCellValue,
  dataTestid,
  textStyle,
  onKeyDown,
  isCurrentlyActive,
  manageCellActiveStatus,
}: ICell) => {
  const handleClick = useCallback(() => {
    if (editable) {
      manageCellActiveStatus &&
        manageCellActiveStatus({
          activate: true,
        });
    }
  }, [editable, manageCellActiveStatus]);

  const handleSave = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangeCellValue &&
        onChangeCellValue({ value: event.target.value, key });
    },
    [onChangeCellValue, key]
  );

  const onTabClick = useCallback(
    (event: React.KeyboardEvent<HTMLTableCellElement>) => {
      if (event.key === "Tab") {
        event.preventDefault();
        onKeyDown && onKeyDown(event);
      }
    },
    [onKeyDown]
  );

  const styles = useMemo(
    () => ({
      border: `1px double ${colors.blue40}`,
      boxShadow: isCurrentlyActive
        ? `inset 0 0 0 2px ${colors.blue100}`
        : "none",
      padding: 2,
      "@media print": { color: colors.black },
    }),
    [isCurrentlyActive]
  );

  return (
    <TableCell
      key={key}
      colSpan={colSpan}
      sx={styles}
      align={textAlign}
      onClick={handleClick}
      onKeyDown={onTabClick}
      data-testid="table-column-cell"
      data-test-id={`${key.toLowerCase().replaceAll(" ", "-")}-cell`}
    >
      {!isCurrentlyActive && (
        <Text
          whiteSpace="pre-line"
          variant="text2"
          data-testid="cell-value"
          sx={textStyle}
        >
          {value}
        </Text>
      )}
      {editable && isCurrentlyActive && (
        <ClickAwayListener
          onClickAway={() =>
            manageCellActiveStatus &&
            manageCellActiveStatus({ activate: false })
          }
        >
          <TextField
            autoFocus
            defaultValue={value}
            onChange={handleSave}
            data-testid="edit-data-cell-value-field"
            multiline
            sx={{
              width: "100%",
              minWidth: "200px",
              // These styles are effecting the base MUI styles for the TextField
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiInputBase-inputMultiline": {
                padding: 0,
              },
              "& input": {
                padding: 0,
              },
            }}
          />
        </ClickAwayListener>
      )}
    </TableCell>
  );
};

export default Cell;
